// Traceability + deterministic stable IDs (issue #512, FR-6.2 / FR-6.3).
//
// Every crafted spec line must trace to a source — a `task.json` field or a ledgered answer
// id. A line with no source is an invented requirement and blocks the craft step
// (traceability is fixed-spine, never switchable). The trace stages with the run until freeze
// carries it into the frozen spec, so A5 (spec-vs-code) can read it long-term (issue #581).
//
// Stable IDs carry forward by SOURCE LINEAGE, deterministically: on a re-craft a requirement
// inherits its prior id when it traces to the SAME source (an exact reference match, never
// fuzzy content matching); a genuinely new source gets a fresh id; a removed source retires
// its id, which is never reused. This exactness is what lets A5 track a requirement over time.

import { readFrozenSpecificationBody, readStagedJson, writeStagedJson } from './run-store.js';

/** A requirement kind — the id prefix. */
export type RequirementKind = 'FR' | 'NFR' | 'AC' | 'INV';

/** One trace row: a stable id, its kind, and the source it derives from. */
export interface TraceEntry {
  id: string;
  kind: RequirementKind;
  source: string;
}

/** The `trace.json` artifact. */
export interface TraceArtifact {
  entries: TraceEntry[];
}

/** A requirement the current craft produced, before an id is assigned. */
export interface RequirementSource {
  kind: RequirementKind;
  source: string;
}

const KINDS: readonly RequirementKind[] = ['FR', 'NFR', 'AC', 'INV'];

function idNumber(id: string): number {
  const m = /-(\d+)$/.exec(id);
  return m ? Number(m[1]) : 0;
}

/**
 * Assign stable ids to the current requirements, carrying prior ids forward by source
 * lineage (exact match on `kind`+`source`). A new source gets the next free number for its
 * kind; a retired source's number is never reused. Fully deterministic.
 */
export function carryForwardIds(
  current: RequirementSource[],
  previous: TraceArtifact | null,
): TraceArtifact {
  const priorBySource = new Map<string, string>(); // "kind source" -> id
  const maxByKind = new Map<RequirementKind, number>();
  for (const kind of KINDS) maxByKind.set(kind, 0);

  for (const entry of previous?.entries ?? []) {
    priorBySource.set(`${entry.kind} ${entry.source}`, entry.id);
    const n = idNumber(entry.id);
    if (n > (maxByKind.get(entry.kind) ?? 0)) {
      maxByKind.set(entry.kind, n);
    }
  }

  const entries: TraceEntry[] = [];
  for (const req of current) {
    const key = `${req.kind} ${req.source}`;
    const inherited = priorBySource.get(key);
    if (inherited !== undefined) {
      entries.push({ id: inherited, kind: req.kind, source: req.source });
      continue;
    }
    const next = (maxByKind.get(req.kind) ?? 0) + 1;
    maxByKind.set(req.kind, next);
    entries.push({ id: `${req.kind}-${next}`, kind: req.kind, source: req.source });
  }
  return { entries };
}

export interface TraceValidation {
  ok: boolean;
  /** Ids with a missing or empty source — invented requirements that block the craft. */
  unsourced: string[];
}

/**
 * Validate that every trace entry carries a non-empty source (FR-6.2). An unsourced line is
 * an invented requirement and blocks the craft step, naming the line.
 */
export function validateTrace(trace: TraceArtifact): TraceValidation {
  const unsourced = trace.entries
    .filter((e) => typeof e.source !== 'string' || e.source.trim().length === 0)
    .map((e) => e.id);
  return { ok: unsourced.length === 0, unsourced };
}

/** Every requirement id a crafted spec markdown declares (FR/NFR/AC/INV). */
export function extractRequirementIds(markdown: string): string[] {
  const ids = new Set<string>();
  const re = /\b((?:FR|NFR|AC|INV)-\d+)\b/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(markdown)) !== null) ids.add(m[1]!);
  return [...ids];
}

/** The result of the FR-8.2 craft-trace gate. */
export interface CraftTraceValidation {
  ok: boolean;
  /** The actionable errors — empty when the trace is complete. */
  errors: string[];
}

/**
 * The craft-trace gate (issue #547, FR-8.2). Given the crafted spec, the trace the agent handed
 * back, and the accepted expert finding ids, refuse when:
 *   - any trace entry has an empty source (an invented requirement);
 *   - a requirement line in the spec has no trace entry (it traces to nothing);
 *   - an accepted expert finding reaches neither the spec (as a trace source) nor is it declined.
 * Deterministic; zero model tokens.
 */
export function validateCraftTrace(
  specMarkdown: string,
  trace: TraceArtifact,
  acceptedFindingIds: readonly string[] = [],
): CraftTraceValidation {
  const errors: string[] = [];

  const unsourced = validateTrace(trace);
  for (const id of unsourced.unsourced) {
    errors.push(
      `line "${id}" has no source; every requirement must trace to the task, an answer, or an expert finding`,
    );
  }

  const tracedIds = new Set(trace.entries.map((entry) => entry.id));
  for (const id of extractRequirementIds(specMarkdown)) {
    if (!tracedIds.has(id)) {
      errors.push(
        `line "${id}" has no source; every requirement must trace to the task, an answer, or an expert finding`,
      );
    }
  }

  const tracedSources = new Set(trace.entries.map((entry) => entry.source));
  const unreflected = acceptedFindingIds.filter((id) => !tracedSources.has(id));
  if (unreflected.length > 0) {
    errors.push(`accepted expert findings not reflected in the spec: ${unreflected.join(', ')}`);
  }

  return { ok: errors.length === 0, errors };
}

/** Stage the run's trace until freeze. */
export function writeTrace(projectRoot: string, dirName: string, trace: TraceArtifact): void {
  writeStagedJson(projectRoot, dirName, 'trace', trace);
}

/**
 * Read the run's trace: the staged one before freeze, else the frozen `specification.json` trace
 * map (issue #581), else the trace an older record kept under `provenance`. Null when none exists,
 * so a re-craft after freeze still carries the stable ids forward.
 */
export function readTrace(projectRoot: string, dirName: string): TraceArtifact | null {
  const parsed = readStagedJson<TraceArtifact>(projectRoot, dirName, 'trace');
  if (parsed !== null && Array.isArray(parsed.entries)) return parsed;
  const frozen = readFrozenSpecificationBody(projectRoot, dirName);
  const map = frozen?.trace;
  if (typeof map === 'object' && map !== null && !Array.isArray(map)) {
    return traceFromMap(map as Record<string, unknown>);
  }
  const legacy = (frozen?.provenance as { trace?: TraceArtifact } | undefined)?.trace;
  return legacy !== undefined && Array.isArray(legacy.entries) ? legacy : null;
}

/** Rebuild trace entries from a frozen trace map, keeping only well-formed requirement ids. */
function traceFromMap(map: Record<string, unknown>): TraceArtifact {
  const entries: TraceEntry[] = [];
  for (const [id, source] of Object.entries(map)) {
    const kind = kindOf(id);
    if (kind !== null && typeof source === 'string') entries.push({ id, kind, source });
  }
  return { entries };
}

function kindOf(id: string): RequirementKind | null {
  const prefix = id.split('-')[0] as RequirementKind;
  return KINDS.includes(prefix) ? prefix : null;
}

/**
 * Parse a raw trace object (from the agent's handed-back file), deriving each entry's kind from
 * its id prefix. Returns null when malformed — a non-object, a missing `entries[]`, or an entry
 * without a string id/source or with an id that is not an FR/NFR/AC/INV line.
 */
export function parseTraceArtifact(raw: unknown): TraceArtifact | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const entries = (raw as Record<string, unknown>).entries;
  if (!Array.isArray(entries)) return null;
  const out: TraceEntry[] = [];
  for (const entry of entries) {
    if (typeof entry !== 'object' || entry === null) return null;
    const { id, source } = entry as Record<string, unknown>;
    if (typeof id !== 'string' || typeof source !== 'string') return null;
    const kind = kindOf(id);
    if (kind === null) return null;
    out.push({ id, kind, source });
  }
  return { entries: out };
}
